import { AnyAction, Dispatch } from 'redux';
import { setPersonWithHandler } from '../../../../global/ducks/person-prc-api';
import { ApiStatuses } from '../../../../global/models/api-model';
import { DemographicAppKeys } from '../../../../global/models/demographics-model';
import { Person } from '../../../../global/models/person-model';
import { Dependent, DependentsActionTypes, DependentsAppState, DependentsState, DependentTypes } from './dependents-model';

const InitialState: DependentsState = {
	dependents: {
		children: [],
		domesticPartners: [],
	},
	childrenFetched: false,
	domesticPartnersFetched: false,
	loadingDependentPersons: 0,
	postDependentStatus: ApiStatuses.None,
	patchDependentStatus: ApiStatuses.None,
};

const InitialAppState: DependentsAppState = {
	[DemographicAppKeys.myCPG]: InitialState,
	[DemographicAppKeys.arc]: InitialState,
};

// iterates over array, and updates person property if there is a client number match
const updateDependentArray = (dependentArray: Dependent[], fetchedPerson: Person) => {
	return dependentArray.map((dependent: Dependent) => {
		if (dependent.person_guid !== fetchedPerson.person_guid) {
			return dependent;
		}

		return {
			...dependent,
			first_name: fetchedPerson.first_name,
			last_name: fetchedPerson.last_name,
			person: fetchedPerson,
		};
	});
};

const updateDependentItem = (dependents: Dependent[], updatedDependent: Dependent) => {
	return dependents.map((dependent: Dependent) => {
		if (dependent.person_guid !== updatedDependent.person_guid) {
			return dependent;
		}

		return {
			...updatedDependent,
			person: dependent.person,
		};
	});
};

export const dependentsReducerByApp = (state = InitialAppState, action: AnyAction): DependentsAppState => {
	const { app } = action;

	switch (action.type) {

		case DependentsActionTypes.SET_CHILDREN:
			return {
				...state,
				[app]: {
					...state[app],
					dependents: {
						...state[app].dependents,
						children: action.payload.data,
					},
					childrenFetched: true,
				},
			};
		case DependentsActionTypes.SET_DOMESTIC_PARTNERS:
			return {
				...state,
				[app]: {
					...state[app],
					dependents: {
						...state[app].dependents,
						domesticPartners: action.payload.data,
					},
					domesticPartnersFetched: true,
				},
			};
		case DependentsActionTypes.UPDATE_DEPENDENT:
			return {
				...state,
				[app]: {
					...state[app],
					dependents: {
						children: action.payload.dependentType === DependentTypes.child
							? updateDependentItem(state[app].dependents.children, action.payload.dependent)
							: state[app].dependents.children,
						domesticPartners: action.payload.dependentType === DependentTypes.domesticPartner
							? updateDependentItem(state[app].dependents.domesticPartners, action.payload.dependent)
							: state[app].dependents.domesticPartners,
					},
				},
			};
		case DependentsActionTypes.UPDATE_DEPENDENT_PERSON:
			return {
				...state,
				[app]: {
					...state[app],
					dependents: {
						children: action.payload.dependentType === DependentTypes.child
							? updateDependentArray(state[app].dependents.children, action.payload.person)
							: state[app].dependents.children,
						domesticPartners: action.payload.dependentType === DependentTypes.domesticPartner
							? updateDependentArray(state[app].dependents.domesticPartners, action.payload.person)
							: state[app].dependents.domesticPartners,
					},
				},
			};
		case DependentsActionTypes.SET_DEPENDENT_PERSON:
			return {
				...state,
				[app]: {
					...state[app],
					dependents: {
						children: action.payload.dependentType === DependentTypes.child
							? updateDependentArray(state[app].dependents.children, action.payload.person)
							: state[app].dependents.children,
						domesticPartners: action.payload.dependentType === DependentTypes.domesticPartner
							? updateDependentArray(state[app].dependents.domesticPartners, action.payload.person)
							: state[app].dependents.domesticPartners,
					},
					childrenFetched: action.payload.dependentType === DependentTypes.child ? true : state[app].childrenFetched,
					domesticPartnersFetched: action.payload.dependentType === DependentTypes.domesticPartner ? true : state[app].domesticPartnersFetched,
					loadingDependentPersons: state[app].loadingDependentPersons - 1,
				},
			};
		case DependentsActionTypes.ADD_LOADING_DEPENDENT_PERSON:
			return {
				...state,
				[app]: {
					...state[app],
					loadingDependentPersons: state[app].loadingDependentPersons + 1,
				},
			};
		case DependentsActionTypes.POST_DEPENDENT_STATUS:
			return {
				...state,
				[app]: {
					...state[app],
					postDependentStatus: action.payload,
				},
			};
		case DependentsActionTypes.PATCH_DEPENDENT_STATUS:
			return {
				...state,
				[app]: {
					...state[app],
					patchDependentStatus: action.payload,
				},
			};
		case DependentsActionTypes.UNLOAD_DEPENDENT_STATE:
			return {
				...state,
				[app]: {
					dependents: {
						children: [],
						domesticPartners: [],
					},
					childrenFetched: false,
					domesticPartnersFetched: false,
					loadingDependentPersons: 0,
					postDependentStatus: ApiStatuses.None,
					patchDependentStatus: ApiStatuses.None,
				},
			};
		case DependentsActionTypes.REMOVE_CHILD:
			return {
				...state,
				[app]: {
					...state[app],
					dependents: {
						children: state[app]?.dependents?.children?.filter(a => a?.person_guid !== action.payload),
						domesticPartners: state[app]?.dependents?.domesticPartners,
					},
				},
			};
		default: return state;
	}
};

export const updateDependent = (dependent: Dependent, dependentType: DependentTypes, app: string) => {
	return {
		type: DependentsActionTypes.UPDATE_DEPENDENT,
		payload: {
			dependent,
			dependentType,
		},
		app,
	};
};

export const updateDependentPerson = (person: Person, dependentType: DependentTypes, app: string) => {
	return {
		type: DependentsActionTypes.UPDATE_DEPENDENT_PERSON,
		payload: {
			person,
			dependentType,
		},
		app,
	};
};

const addLoadingDependentPerson = (app: string) => {
	return {
		type: DependentsActionTypes.ADD_LOADING_DEPENDENT_PERSON,
		app,
	};
};

export const setDependentPerson = (person: Person, dependentType: DependentTypes, app: string) => {
	return {
		type: DependentsActionTypes.SET_DEPENDENT_PERSON,
		payload: {
			person,
			dependentType,
		},
		app,
	};
};

export const setChildren = (response: any, app: string) => {

	return {
		type: DependentsActionTypes.SET_CHILDREN,
		payload: response,
		app,
	};
};


export const setPostDependentStatus = (status: ApiStatuses, app: string) => {
	return {
		type: DependentsActionTypes.POST_DEPENDENT_STATUS,
		payload: status,
		app,
	};
};

export const setPatchDependentStatus = (status: ApiStatuses, app: string) => {
	return {
		type: DependentsActionTypes.PATCH_DEPENDENT_STATUS,
		payload: status,
		app,
	};
};

export const setDomesticPartners = (response: any, app: string) => {

	return {
		type: DependentsActionTypes.SET_DOMESTIC_PARTNERS,
		payload: response,
		app,
	};
};


const fetchDependentPeople = (dispatch: Dispatch<any>, dependents: Dependent[], dependentType: DependentTypes, app: string) => {
	const fetchedIds = new Set<string>();

	// fetch person data for each spouse
	for (let i = 0; i < dependents.length; i += 1) {
		const dependent = dependents[i];

		// only fetch one person per client number
		if (!fetchedIds.has(dependent.person_guid)) {
			fetchedIds.add(dependent.person_guid);

			dispatch(addLoadingDependentPerson(app));

			if (dependentType === DependentTypes.child) {
				dispatch(setPersonWithHandler(dependent.person_guid, (personResponse) => dispatch(setDependentPerson(personResponse.data, DependentTypes.child, app))));
			} else if (dependentType === DependentTypes.domesticPartner) {
				dispatch(setPersonWithHandler(dependent.person_guid, (personResponse) => dispatch(setDependentPerson(personResponse.data, DependentTypes.domesticPartner, app))));
			}
		}
	}
};

export const dispatchSetChildren = (dispatch: Dispatch<any>, app: string) => (response) => {

	// fetch person data for each spouse
	const dependents: Dependent[] = response.data;
	fetchDependentPeople(dispatch, dependents, DependentTypes.child, app);

	dispatch(setChildren(response, app));
};


export const dispatchSetChildrenFailed = (dispatch: Dispatch<any>, app: string) => {
	dispatch(setChildren({ data: [] }, app));
};

export const dispatchSetDomesticPartners = (dispatch: Dispatch<any>, app: string) => (response) => {

	// fetch person data for each domestic partner
	const dependents: Dependent[] = response.data;
	fetchDependentPeople(dispatch, dependents, DependentTypes.domesticPartner, app);

	dispatch(setDomesticPartners(response, app));
};


export const dispatchSetDomesticPartnersFailed = (dispatch: Dispatch<any>, app: string) => {
	dispatch(setDomesticPartners({ data: [] }, app));
};

export const unloadDependentState = (app: string) => {

	return {
		type: DependentsActionTypes.UNLOAD_DEPENDENT_STATE,
		app,
	};
};

export const removeChild = (guid: string, app: string) => {

	return {
		type: DependentsActionTypes.REMOVE_CHILD,
		payload: guid,
		app,
	};
};